/**
 * Per-blend-file scene cache backed by IndexedDB.
 *
 * Save-only path: a scene snapshot is captured into a `RawSceneDocument`,
 * handed to the persistence worker for encode + JSON.stringify + IDB write,
 * and that's it. The cache exists for crash recovery via the session store
 * (see `sessionCache.ts`); the load path lives there. Direct cache loads
 * have no callers and are not exposed here.
 */

import type { AppState } from "../store.ts";
import {
  clearAllScenes,
  deleteSceneRecord,
  getAllSceneRecords,
  MAX_SCENE_BYTES,
  MAX_TOTAL_BYTES,
  type SceneRecord,
  totalBytes,
} from "./sceneDB.ts";
import { captureSceneDocument } from "./sceneDocument.ts";
import { saveSceneCache as workerSaveSceneCache } from "./sceneDocumentIO.ts";

// ---------------------------------------------------------------------------
// Storage keys
// ---------------------------------------------------------------------------

const STORAGE_PREFIX = "poseref_scene_";

export function storageKey(fileName: string): string {
  return STORAGE_PREFIX + fileName.replace(/[^a-zA-Z0-9._-]/g, "_");
}

// ---------------------------------------------------------------------------
// Error reporting
// ---------------------------------------------------------------------------

export let onSaveError: ((error: unknown) => void) | null = null;

export function setOnSaveError(cb: ((error: unknown) => void) | null): void {
  onSaveError = cb;
}

export class SceneTooLargeError extends Error {
  constructor(sizeBytes: number) {
    super(
      `Scene is ${(sizeBytes / 1024 / 1024).toFixed(1)} MB — exceeds the ` +
        `${MAX_SCENE_BYTES / 1024 / 1024} MB per-scene limit for local backup.`,
    );
    this.name = "SceneTooLargeError";
  }
}

export class StorageFullError extends Error {
  constructor() {
    super(
      `Local backup storage is full (${MAX_TOTAL_BYTES / 1024 / 1024} MB limit).`,
    );
    this.name = "StorageFullError";
  }
}

// ---------------------------------------------------------------------------
// Eviction
// ---------------------------------------------------------------------------

const MAX_CACHED_SCENES = 5;

export interface SceneCacheEntry {
  name: string;
  sizeBytes: number;
  savedAt: number | null;
}

export async function listSavedScenesDetailed(): Promise<SceneCacheEntry[]> {
  try {
    const records = await getAllSceneRecords();
    return records.map((r: SceneRecord) => ({
      name: r.key.slice(STORAGE_PREFIX.length),
      sizeBytes: r.sizeBytes,
      savedAt: r.savedAt ?? null,
    }));
  } catch {
    return [];
  }
}

export async function evictOldSceneBackups(
  currentKey: string,
  maxToEvict = 1,
): Promise<number> {
  const entries = await listSavedScenesDetailed();
  entries.sort((a, b) => (a.savedAt ?? 0) - (b.savedAt ?? 0));
  let evicted = 0;
  for (const entry of entries) {
    if (evicted >= maxToEvict) break;
    const key = STORAGE_PREFIX + entry.name;
    if (key === currentKey) continue;
    try {
      await deleteSceneRecord(key);
      evicted++;
    } catch {
      // ignore
    }
  }
  return evicted;
}

async function enforceSceneCacheLimit(currentKey: string): Promise<void> {
  const entries = await listSavedScenesDetailed();
  if (entries.length <= MAX_CACHED_SCENES) return;
  entries.sort((a, b) => (a.savedAt ?? 0) - (b.savedAt ?? 0));
  const toEvict = entries.length - MAX_CACHED_SCENES;
  let evicted = 0;
  for (const entry of entries) {
    if (evicted >= toEvict) break;
    const key = STORAGE_PREFIX + entry.name;
    if (key === currentKey) continue;
    try {
      await deleteSceneRecord(key);
      evicted++;
    } catch {
      // ignore
    }
  }
}

// ---------------------------------------------------------------------------
// Pre-flight size estimation
// ---------------------------------------------------------------------------

/**
 * Estimate the serialized byte size of a raw scene snapshot.
 *
 * Typed arrays become ~1.37× larger when base64-encoded, and the JSON
 * envelope adds modest overhead. This is a conservative upper bound used
 * for pre-flight gating before sending to the worker — it lets us reject
 * obviously-oversized scenes without paying for the encode pass.
 */
function estimateRawSize(val: unknown): number {
  if (val == null) return 4;
  if (typeof val === "string") return val.length * 2 + 2;
  if (typeof val === "number" || typeof val === "boolean") return 8;
  if (ArrayBuffer.isView(val)) {
    return Math.ceil((val as ArrayBufferView).byteLength / 3) * 4 * 2 + 2;
  }
  if (Array.isArray(val)) {
    let s = 2;
    for (const item of val) s += estimateRawSize(item);
    return s;
  }
  if (typeof val === "object") {
    let s = 2;
    for (const [k, v] of Object.entries(val as Record<string, unknown>)) {
      if (v === undefined) continue;
      s += k.length * 2 + 4 + estimateRawSize(v);
    }
    return s;
  }
  return 0;
}

// ---------------------------------------------------------------------------
// Save
// ---------------------------------------------------------------------------

export async function saveScene(
  fileName: string,
  state: AppState,
): Promise<void> {
  if (state.blendFiles.size === 0) return;
  const raw = captureSceneDocument(state);
  // Live camera position/target are not persisted in the auto-backup —
  // they change on every orbit drag and aren't meaningful crash-recovery
  // data (bookmarks cover intentional saves). Matches saveSessionState.
  raw.cameraPosition = null;
  raw.cameraTarget = null;
  const key = storageKey(fileName);

  const estimatedBytes = estimateRawSize(raw);
  if (estimatedBytes > MAX_SCENE_BYTES) {
    onSaveError?.(new SceneTooLargeError(estimatedBytes));
    return;
  }

  await enforceSceneCacheLimit(key);

  const used = await totalBytes();
  if (used + estimatedBytes > MAX_TOTAL_BYTES) {
    await evictOldSceneBackups(key, 3);
    const usedAfter = await totalBytes();
    if (usedAfter + estimatedBytes > MAX_TOTAL_BYTES) {
      onSaveError?.(new StorageFullError());
      return;
    }
  }

  try {
    await workerSaveSceneCache(key, raw);
  } catch (e) {
    onSaveError?.(e);
  }
}

// ---------------------------------------------------------------------------
// Delete / list
// ---------------------------------------------------------------------------

export async function deleteScene(fileName: string): Promise<void> {
  try {
    await deleteSceneRecord(storageKey(fileName));
  } catch {
    // ignore
  }
}

export async function deleteAllScenes(): Promise<void> {
  try {
    await clearAllScenes();
  } catch {
    // ignore
  }
}

export function listSavedScenes(): Promise<string[]> {
  return listSavedScenesDetailed().then((e) => e.map((x) => x.name));
}

export async function estimateCacheSize(): Promise<number> {
  try {
    return await totalBytes();
  } catch {
    return 0;
  }
}
